/**
 * Client for the B3 management endpoints — custom roles
 * (`/api/workspaces/:slug/roles`) and feature-flag administration
 * (`/api/feature-flags`). Thin wrappers over `apiRequest`; flag responses are
 * snake_case on the wire and camelized for the UI.
 */

import { apiRequest } from "../agents/api/client";
import { camelizeKeys } from "../tables/api/camelize";

export interface BuiltinRole {
  name: string;
  permissions: string[];
}
export interface CustomRole {
  id: string;
  name: string;
  permissions: string[];
  /** 'helpdesk' roles are authored on the desk's Roles page; workspace
   *  surfaces show them with a scope badge. Absent on older responses. */
  scope?: "workspace" | "helpdesk";
}
export interface RoleList {
  builtin: BuiltinRole[];
  custom: CustomRole[];
}
export interface PermissionGroup {
  subject: string;
  permissions: string[];
}

export type FlagValue = boolean | number | string;
export interface AdminFlag {
  key: string;
  kind: string;
  description: string;
  internalOnly: boolean;
  defaultValue: FlagValue;
  value: FlagValue;
  source: "workspace" | "org" | "default";
}

export const rolesKeys = {
  list: (slug: string) => ["roles", slug] as const,
  catalogue: (slug: string) => ["roles", slug, "catalogue"] as const,
};

// ----- Per-resource permissions (rbac-permissions §3.2) -----

/** A workflow/query the admin can grant per-resource permissions on. */
export interface ResourceRef {
  id: string;
  name: string;
}
export interface WorkflowPerm {
  workflowId: string;
  run: boolean;
  signal: boolean;
  cancel: boolean;
  publish: boolean;
  manage: boolean;
}
export interface QueryPerm {
  storedQueryId: string;
  run: boolean;
  edit: boolean;
}

export const resourcePermsKeys = {
  workflows: (slug: string) => ["resource-perms", slug, "workflows"] as const,
  queries: (slug: string) => ["resource-perms", slug, "queries"] as const,
  roleWorkflow: (slug: string, roleKey: string) =>
    ["resource-perms", slug, roleKey, "workflow"] as const,
  roleQuery: (slug: string, roleKey: string) => ["resource-perms", slug, roleKey, "query"] as const,
};

/** All workflows in the workspace (id + name), for the access matrix rows. */
export function listWorkspaceWorkflows(): Promise<ResourceRef[]> {
  return apiRequest<{ items: Array<{ id: string; name: string }> }>(
    "GET",
    "/api/workflows/definitions",
  ).then((r) => (r.items ?? []).map((w) => ({ id: w.id, name: w.name })));
}
/** All stored queries in the workspace (id + name). */
export function listWorkspaceQueries(): Promise<ResourceRef[]> {
  return apiRequest<{ items: Array<{ id: string; name: string }> }>(
    "GET",
    "/api/data-queries",
    undefined,
    { limit: 100 },
  ).then((r) => (r.items ?? []).map((q) => ({ id: q.id, name: q.name })));
}

export function getRoleWorkflowPerms(slug: string, roleKey: string): Promise<WorkflowPerm[]> {
  return apiRequest<WorkflowPerm[]>(
    "GET",
    `/api/workspaces/${slug}/roles/${roleKey}/workflow-permissions`,
  );
}
export function setRoleWorkflowPerms(
  slug: string,
  roleKey: string,
  entries: WorkflowPerm[],
): Promise<WorkflowPerm[]> {
  return apiRequest<WorkflowPerm[]>(
    "PUT",
    `/api/workspaces/${slug}/roles/${roleKey}/workflow-permissions`,
    { entries },
  );
}
export function getRoleQueryPerms(slug: string, roleKey: string): Promise<QueryPerm[]> {
  return apiRequest<QueryPerm[]>(
    "GET",
    `/api/workspaces/${slug}/roles/${roleKey}/query-permissions`,
  );
}
export function setRoleQueryPerms(
  slug: string,
  roleKey: string,
  entries: QueryPerm[],
): Promise<QueryPerm[]> {
  return apiRequest<QueryPerm[]>(
    "PUT",
    `/api/workspaces/${slug}/roles/${roleKey}/query-permissions`,
    { entries },
  );
}
export const flagsKeys = {
  list: () => ["feature-flags"] as const,
};

export function listRoles(slug: string): Promise<RoleList> {
  return apiRequest<RoleList>("GET", `/api/workspaces/${slug}/roles`);
}
export function listPermissionCatalogue(slug: string): Promise<{ groups: PermissionGroup[] }> {
  return apiRequest<{ groups: PermissionGroup[] }>(
    "GET",
    `/api/workspaces/${slug}/roles/catalogue`,
  );
}
export function createRole(
  slug: string,
  body: { name: string; permissions: string[] },
): Promise<CustomRole> {
  return apiRequest<CustomRole>("POST", `/api/workspaces/${slug}/roles`, body);
}
export function updateRole(
  slug: string,
  id: string,
  body: { name: string; permissions: string[] },
): Promise<CustomRole> {
  return apiRequest<CustomRole>("PATCH", `/api/workspaces/${slug}/roles/${id}`, body);
}
export function deleteRole(slug: string, id: string): Promise<{ repointed: number }> {
  return apiRequest<{ repointed: number }>("DELETE", `/api/workspaces/${slug}/roles/${id}`);
}

export function listFlags(): Promise<{ flags: AdminFlag[] }> {
  return apiRequest<unknown>("GET", "/api/feature-flags").then(
    camelizeKeys<{ flags: AdminFlag[] }>,
  );
}
export function setFlag(
  key: string,
  body: { value: FlagValue; scope?: "workspace" | "org" },
): Promise<unknown> {
  return apiRequest<unknown>("PUT", `/api/feature-flags/${encodeURIComponent(key)}`, body);
}
export function clearFlag(key: string, scope?: "workspace" | "org"): Promise<unknown> {
  return apiRequest<unknown>(
    "DELETE",
    `/api/feature-flags/${encodeURIComponent(key)}`,
    undefined,
    scope ? { scope } : undefined,
  );
}
